// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState, useEffect } from 'react';
import { getCurrentUser } from '../services/userService';
import { deriveEffectiveRole } from '../services/authService';
import { useSiteTime } from '../hooks/useSiteTime';
import './Profile.css';

interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  /** Canonical UserRole resolved from the /me `roles` array, or null when the
   *  user carries no role the UI recognizes. */
  role: string | null;
  created_at: string;
}

function Profile() {
  const { format } = useSiteTime();
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const data = await getCurrentUser();
      // /me relays IAM's `roles` array; collapse it to the single display role.
      setUser({ ...data, role: deriveEffectiveRole(data) } as UserProfile);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="loading-state">Loading profile...</div>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="error-state">Error: {error || 'User not found'}</div>
        </div>
      </div>
    );
  }

  const formatRole = (role: string | null): string => {
    if (!role) return 'No role assigned';
    return role
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getInitials = (firstName: string, lastName: string): string => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return first + last || 'U';
  };

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-avatar-large">{getInitials(user.first_name, user.last_name)}</div>
          <div className="profile-info">
            <h1 className="profile-username">
              {user.first_name} {user.last_name}
            </h1>
            <p className="profile-role">{formatRole(user.role)}</p>
          </div>
        </div>

        <div className="profile-details">
          <div className="detail-section">
            <h2>Account Information</h2>
            <div className="detail-row">
              <span className="detail-label">User ID:</span>
              <span className="detail-value">{user.id}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Email:</span>
              <span className="detail-value">{user.email}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Role:</span>
              <span className="detail-value">{formatRole(user.role)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Created:</span>
              <span className="detail-value">{format(user.created_at, 'dateTime')}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Profile;
